/**
 * NotificationPrompt — convite "Receber notificações?" (Web Push, Story X1.2.2).
 *
 * Fica montado globalmente (App.tsx, no shell autenticado) e se auto-gate:
 * só renderiza quando o usuário tem `Notification.permission === "default"`,
 * já abriu o app ≥ 3 vezes, o navegador suporta Web Push, ainda não há
 * subscription e o dismiss de 30d não está ativo (toda a decisão em
 * lib/notificationPrompt.ts via useNotificationPermission).
 *
 * Reusa o motor existente usePushSubscription (PRD-1 §1.6.3): "Ativar" chama
 * subscribe(), que faz requestPermission → registra SW → busca VAPID →
 * pushManager.subscribe → POST /push/subscribe. Nenhum backend novo.
 *
 * Posicionamento: card flutuante no canto inferior direito em ≥sm; no mobile,
 * banner full-width com offset (`bottom-[5.5rem]`) para não colidir com o
 * InstallBanner (que ocupa `bottom-0`, `sm:hidden`) no caso raro de ambos
 * elegíveis ao mesmo tempo.
 */
import { useCallback } from "react";
import { Bell, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useNotificationPermission } from "@/hooks/useNotificationPermission";
import { PUSH_BASE_INTERNO, usePushSubscription } from "@/hooks/usePushSubscription";
import { useServerHasVapid } from "@/hooks/useServerHasVapid";
import { lerIntencaoDoPush } from "@/lib/push/memoriaDoPush";

export interface NotificationPromptProps {
  /** Prefixo das rotas de push. O Portal do Parceiro passa `PUSH_BASE_PORTAL`
   * porque o middleware DEFAULT DENY não o deixa alcançar `/push/*` (UI-4b). */
  basePath?: string;
}

export function NotificationPrompt({ basePath = PUSH_BASE_INTERNO }: NotificationPromptProps = {}) {
  const { isSupported, permission, subscription, subscribe, isSubscribing, error } =
    usePushSubscription(basePath);

  // Sem chave VAPID no servidor, conceder permissão não traria notificação
  // nenhuma — e a chance de perguntar é única. Ver o docstring do hook.
  const serverHasVapid = useServerHasVapid(basePath);

  const { shouldPrompt, dismiss } = useNotificationPermission({
    isSupported,
    permission,
    alreadySubscribed: subscription !== null,
    serverHasVapid,
    // Aparelho em que a pessoa já ligou o push: o convite não pergunta de novo (se o push parou, o `AvisoDoPush` toma o lugar). Lido a
    // cada render, e o `usePushSubscription` re-renderiza quando algo muda o push do aparelho (`seweb:push-mudou`).
    intencaoLigada: lerIntencaoDoPush() === "ligado",
  });

  const handleEnable = useCallback(async () => {
    // subscribe() é um user gesture (clique) → requestPermission é permitido.
    // Após resolver, permission vira "granted" (sucesso) ou "denied" (recusa);
    // em ambos os casos shouldPrompt passa a false e o convite some sozinho.
    await subscribe();
  }, [subscribe]);

  if (!shouldPrompt) return null;

  return (
    <div
      role="dialog"
      aria-label="Ativar notificações"
      data-testid="notif-prompt"
      className="fixed inset-x-4 bottom-[5.5rem] z-50 rounded-lg border bg-background/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-96"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Bell className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Receber notificações?</p>
          <p className="text-xs text-muted-foreground">
            Ative para ser avisado sobre atendimentos, alertas e atualizações importantes — mesmo
            com o app fechado.
          </p>
          {error && (
            <p
              role="alert"
              data-testid="notif-prompt-error"
              className="mt-1 text-xs text-destructive"
            >
              {error}
            </p>
          )}
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleEnable}
              disabled={isSubscribing}
              data-testid="notif-prompt-enable"
            >
              {isSubscribing ? "Ativando…" : "Ativar"}
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} data-testid="notif-prompt-later">
              Agora não
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dispensar"
          data-testid="notif-prompt-dismiss"
          className="-mr-1 -mt-1 shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default NotificationPrompt;
